import { useCallback, useRef } from 'react';
import Cookies from 'universal-cookie';
import config from 'common/config';
import callApi from 'helpers/apiCaller';
import { faceLogin } from './services';

// update_progress === 1: user phải đổi mật khẩu lần đầu
const FIRST_LOGIN_PROGRESS = 1;
const MAX_FACE_ATTEMPTS = 3;
const LOCKED_ERROR_CODE = 'face_login_locked';

/**
 * Hook xử lý luồng đăng nhập bằng khuôn mặt.
 * Dùng tại màn hình đăng nhập (SignIn).
 *
 * @param {{ onSuccess: Function, onError: Function, onLocked: Function }} options
 * @returns {{ loginWithFaceImage: Function }}
 */
export function useFaceAuth({ onSuccess, onError, onLocked } = {}) {
  const failureCountRef = useRef(0);

  const loginWithFaceImage = useCallback(
    async (file) => {
      const cookies = new Cookies();

      if (!file || !(file instanceof File || file instanceof Blob)) {
        onError?.({ message: 'Ảnh không hợp lệ.' });
        return;
      }

      const userId = window.localStorage.getItem('face_login_user_id');
      if (!userId) {
        onError?.({
          message:
            'Vui lòng đăng nhập bằng mật khẩu lần đầu để kích hoạt tính năng này.'
        });
        return;
      }

      config.showLoader();

      try {
        const res = await faceLogin({ user_id: userId, face_image: file });

        if (!res?.data) {
          config.hideLoader();
          failureCountRef.current += 1;

          const errorData = res?.error;
          const errorCode =
            errorData?.errors?.[0]?.code || errorData?.error_code;
          const isLocked = errorCode === LOCKED_ERROR_CODE;
          const isTooManyAttempts =
            !isLocked && failureCountRef.current >= MAX_FACE_ATTEMPTS;

          if (isLocked) {
            const lockedMsg =
              errorData?.errors?.[0]?.message ||
              'Tài khoản đã bị khóa tính năng đăng nhập bằng khuôn mặt, vui lòng đăng nhập bằng mật khẩu.';
            onLocked?.(lockedMsg);
          } else if (isTooManyAttempts) {
            onLocked?.(
              'Đăng nhập bằng khuôn mặt thất bại quá 3 lần, vui lòng đăng nhập bằng mật khẩu.'
            );
          } else {
            onError?.({ response: { data: errorData, status: res?.status } });
          }
          return;
        }

        const data = res.data?.data || res.data;
        const token = data?.access_token;

        if (!token) {
          config.hideLoader();
          failureCountRef.current += 1;

          if (failureCountRef.current >= MAX_FACE_ATTEMPTS) {
            onLocked?.(
              'Đăng nhập bằng khuôn mặt thất bại quá 3 lần, vui lòng đăng nhập bằng mật khẩu.'
            );
          } else {
            onError?.({
              response: {
                data: {
                  errors: [
                    {
                      message:
                        data?.message || 'Không nhận diện được người dùng.'
                    }
                  ]
                }
              }
            });
          }
          return;
        }

        // Lưu token
        cookies.set('AccessToken', token, { path: '/' });
        window.localStorage.setItem('AccessToken', token);

        const user = data.user;

        // Yêu cầu đổi mật khẩu lần đầu
        if (user?.update_progress === FIRST_LOGIN_PROGRESS) {
          localStorage.setItem('user_update_progress', FIRST_LOGIN_PROGRESS);
          config.hideLoader();
          window.location.href = '/doi-mat-khau';
          return;
        }

        // Fetch thông tin user mới nhất trước, rồi mới redirect
        let finalUser = user;
        if (user) {
          try {
            const userRes = await callApi(
              config.getRootURL() + '/gd/v3/user_infos',
              'GET',
              {},
              { authorization: token }
            );
            if (userRes?.data?.data?.user) {
              finalUser = userRes.data.data.user;
            }
          } catch (_) {
            // Dùng user từ face login response nếu user_infos lỗi
          }
        }

        if (finalUser) {
          const mergedUser = { ...finalUser, face_login_registered: true };
          const safeUser = JSON.stringify(mergedUser).replace(/</g, '\\u003c');
          cookies.set('user_store', safeUser, { path: '/' });
          config.AddDataIntoCache('user_store', '/', safeUser);
          config.AddDataIntoCache('is_logged', '/', true);
          onSuccess?.(mergedUser);
        }
      } catch (err) {
        console.error('Lỗi xác thực khuôn mặt:', err);
        onError?.({ message: 'Có lỗi xảy ra khi xác thực khuôn mặt.' });
      } finally {
        config.hideLoader();
      }
    },
    [onSuccess, onError, onLocked]
  );

  return { loginWithFaceImage };
}
